import { Injectable, Inject } from '@angular/core';
import { Title, Meta, MetaDefinition } from '@angular/platform-browser';
import { MetaConfig, MetaPayload } from './meta.model';
import { META_CONFIG } from './meta.constant';
import { arrayify } from '../core';
import { DOCUMENT } from '@angular/common';

// Tags this service owns across route changes. Their `index.html` values are
// captured at construction and restored whenever a payload omits the field —
// without that, a tag written by one route would stick to every route after it
// (`updateTag` mutates one persistent element in an SPA), so a recipe cover
// would follow the user onto pages that have nothing to do with it.
const MANAGED_TAGS: MetaDefinition[] = [
  { name: 'description' },
  { property: 'og:title' },
  { name: 'twitter:title' },
  { property: 'og:description' },
  { name: 'twitter:description' },
  { property: 'og:image' },
  { name: 'twitter:image' }
];

@Injectable()
export class MetaService {
  // selector -> content as authored in index.html ('' when the app ships no default)
  private _defaults = new Map<string, string>();

  // Non-null while a caller holds `document.title` via `lockTitle()`. `restore`
  // is the tab title captured when the lock was taken; `pending` is the most
  // recent title a page tried to `set()` while locked, flushed on unlock.
  private _titleLock: { restore: string; pending?: string | string[] } | null = null;

  constructor(
    private _title: Title,
    private _meta: Meta,
    @Inject(META_CONFIG)
    private _config: MetaConfig,
    @Inject(DOCUMENT)
    private _document: any
  ) {
    MANAGED_TAGS.forEach((tag) => {
      const selector = this._selectorFor(tag);

      this._defaults.set(selector, this._meta.getTag(selector)?.content ?? '');
    });
  }

  set(meta: MetaPayload) {
    if (meta?.title !== undefined) {
      if (this._titleLock) {
        // A lock holder (the ZCP overlay) owns the browser tab while it's on
        // screen. Remember the page's intended title so `unlockTitle()` can
        // flush it, but don't write it now — the tab must keep identifying the
        // overlay, not the subpage navigated to underneath it. og:/twitter:
        // title tags below still update as normal.
        this._titleLock.pending = meta.title;
      } else {
        this._title.setTitle(
          meta.title
            ? this._getTitle(meta.title)
            : this._config.baseTitle
        );
      }
    }

    // og:title falls back to `title`, mirroring how og:description has always
    // fallen back to `description` — callers pass one title and get both.
    const ogTitle = this._resolveOgTitle(meta);

    this._applyTag({ property: 'og:title' }, ogTitle);
    this._applyTag({ name: 'twitter:title' }, ogTitle);

    // `description` also drives the plain description tag, which this service
    // never wrote before — it only ever reached the og:/twitter: variants.
    this._applyTag({ name: 'description' }, meta?.description);

    const ogDesc = meta?.ogDescription || meta?.description;

    this._applyTag({ property: 'og:description' }, ogDesc);
    this._applyTag({ name: 'twitter:description' }, ogDesc);

    const imageHref = this._resolveAbsoluteUrl(meta?.image);
    const twitterImageHref = meta?.twitterImage
      ? this._resolveAbsoluteUrl(meta.twitterImage)
      : imageHref;

    this._applyTag({ property: 'og:image' }, imageHref);
    this._applyTag({ name: 'twitter:image' }, twitterImageHref);

    if (meta?.viewport) {
      this._meta.updateTag({
        content: meta.viewport,
        name: 'viewport'
      });
    }

    if (this._document?.location?.href) {
      this._meta.updateTag({
        content: this._document.location.href,
        property: 'og:url'
      });
    }

    this._meta.updateTag({
      content: 'website',
      property: 'og:type'
    });

    this._meta.updateTag({
      content: 'summary_large_image',
      name: 'twitter:card'
    });
  }

  /**
   * Takes exclusive ownership of the browser tab title until `unlockTitle()`.
   * While locked, `set()` keeps updating the og:/twitter: title tags but stops
   * writing `document.title`, so the lock holder's title survives page
   * navigations underneath it (the ZCP overlay floats over whatever
   * control-plane subpage the user clicks to). Calling it again while already
   * locked just swaps the shown title — e.g. once the project name resolves —
   * and keeps the original restore snapshot.
   */
  lockTitle(title: string | string[]) {
    if (!this._titleLock) {
      this._titleLock = { restore: this._title.getTitle() };
    }

    this._title.setTitle(
      title
        ? this._getTitle(title)
        : this._config.baseTitle
    );
  }

  /**
   * Releases a `lockTitle()`. If a page called `set({ title })` while the lock
   * was held, that title is applied now (the page the user ended up on wins);
   * otherwise the tab reverts to whatever was showing when the lock was taken.
   * No-op when nothing is locked.
   */
  unlockTitle() {
    if (!this._titleLock) {
      return;
    }

    const { restore, pending } = this._titleLock;

    this._titleLock = null;

    if (pending !== undefined) {
      this.set({ title: pending });
    } else {
      this._title.setTitle(restore);
    }
  }

  /**
   * Writes `content` onto a managed tag, falling back to the `index.html`
   * default when the payload has nothing for it. Removes the tag outright when
   * there is no default either — previously these were written unconditionally,
   * which produced `content="undefined"` on every page that set no image.
   */
  private _applyTag(tag: MetaDefinition, content?: string) {
    const selector = this._selectorFor(tag);
    const resolved = content || this._defaults.get(selector);

    if (resolved) {
      this._meta.updateTag({ ...tag, content: resolved }, selector);
    } else {
      this._meta.removeTag(selector);
    }
  }

  private _selectorFor(tag: MetaDefinition) {
    return tag.name
      ? `name="${tag.name}"`
      : `property="${tag.property}"`;
  }

  private _resolveOgTitle(meta?: MetaPayload): string | undefined {
    const title = meta?.ogTitle !== undefined
      ? meta.ogTitle
      : meta?.title;

    if (title === undefined) {
      return undefined;
    }

    return title
      ? this._getTitle(title)
      : this._config.baseTitle;
  }

  private _resolveOrigin(): string {
    const href = this._document?.location?.href;

    if (!href) {
      return '';
    }

    const parsedHref = href.split('/');

    return parsedHref[0] + '//' + parsedHref[2];
  }

  private _resolveAbsoluteUrl(url?: string): string | undefined {
    if (!url) {
      return undefined;
    }

    // Root-relative paths belong to the app itself; anything else (a recipe
    // cover on GitHub, a Strapi upload) is already absolute and passed through.
    return url.startsWith('/')
      ? `${this._resolveOrigin()}${url}`
      : url;
  }

  private _getTitle(title: string | string[]) {
    return `${arrayify(title).join(` ${this._config.separator} `)} ${this._config.separator} ${this._config.baseTitle}`;
  }
}
